import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { postMessageRN, useReactNativeProps, WebToRNAction } from '@eika-infrastruktur/mobilbank-lib';
import { SigningMethodType } from '@eika-infrastruktur/mobilbank-lib/src/nativeProps';
import { ErrorMessage, InfoMessage } from '@eika/attention-message';
import { Button, ButtonTypes, LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { Icon, IconTypes } from '@eika/icon';
import { Link, LinkType } from '@eika/link';

import { useConfig } from '../../defaultConfig';
import useSigningMethod, { useUpdateSigningMethod } from '../../fetchHooks/useSigningMethod';
import type { BankIdSigningMethod } from '../../generated-types';
import useTranslation, { format } from '../../hooks/useTranslation';
import { settingsTrackIDs } from '../../utils/gtm/trackIDs/settings';
import SelectPanel from './SelectPanel';
import getDefaultSigningMethod from './getDefaultSigningMethod';

import './SelectSigningMethod.scss';

const SelectSigningMethod = () => {
    const { biometrics, loginMethod, supports, signingMethod, deviceId } = useReactNativeProps();

    const { biometricsEnabledApp, biometricsEnabledOS, biometricCapabilities } = biometrics!;
    const translated = useTranslation();
    const navigate = useNavigate();
    const { loginSettingsLink, frontPageLink } = useConfig();

    const [showErrorMessage, setShowErrorMesage] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const pinSigningEnabled = supports?.loginAndSigningMethod;
    const [hasMadeAChoice, setHasMadeAChoice] = useState<boolean>(false);
    const [noSigningMethodChosen, setNoSigningMethodChosen] = useState<boolean>(false);
    const [signingMethodState, setSigningMethodState] = useState<SigningMethodType | undefined>(signingMethod?.type);
    const { data: signingMethodQueryResult } = useSigningMethod(deviceId);

    const mutateSigningMethod = useUpdateSigningMethod({ value: deviceId ?? '' });

    useEffect(() => {
        const defaultSigningMethod = getDefaultSigningMethod(
            loginMethod?.type,
            signingMethodQueryResult?.bankIdSigningMethod,
        );

        setSigningMethodState(signingMethod?.type ?? defaultSigningMethod);
    }, [loginMethod?.type, signingMethod?.type, signingMethodQueryResult?.bankIdSigningMethod]);

    useEffect(() => {
        if (signingMethodState && hasMadeAChoice) {
            setShowSuccess(true);
            window.scrollTo(0, 0);
        }
    }, [hasMadeAChoice, loginSettingsLink, signingMethodState]);

    useEffect(() => {
        if (signingMethodQueryResult && !signingMethod) {
            setSigningMethodState('BANKID');
        }
    }, [signingMethod, signingMethodQueryResult]);

    const handleNavigateToNextStep = () => {
        if (signingMethodState === undefined) {
            setNoSigningMethodChosen(true);
        } else {
            setNoSigningMethodChosen(false);
            mutateSigningMethod.reset();
            if (deviceId) {
                if (signingMethodState === 'BIOMETRIC' && (!biometricsEnabledOS || loginMethod?.type !== 'BIOMETRIC')) {
                    setShowErrorMesage(true);
                } else {
                    postMessageRN({ action: WebToRNAction.SET_SIGNING_METHOD, payload: signingMethodState });
                    if (signingMethodState === 'BANKID') {
                        mutateSigningMethod.mutate({ bankIdSigningMethod: signingMethodState as BankIdSigningMethod });
                    }
                }
            }
            setHasMadeAChoice(true);
        }
    };

    const resetNavigation = () => {
        if (loginSettingsLink) {
            window.location.href = loginSettingsLink.url;
        } else {
            navigate('/', { replace: true });
        }
    };

    if (showSuccess) {
        return (
            <div className="success-parent">
                <Icon type={IconTypes.USP_CHECKMARK_36} className="icon-success" />
                <h2 className="header-success">{translated.loginAndSigning.headerSuccess}</h2>
                {frontPageLink && (
                    <Link
                        linkType={LinkType.BUTTON_STANDARD_POSITIVE}
                        className="signing-method-button-success"
                        href={frontPageLink.url}
                    >
                        {translated.loginAndSigning.toFrontPage}
                    </Link>
                )}
            </div>
        );
    }

    return (
        <>
            <div className="login-method-row">
                <h2 className="login-method-subtitle">{translated.loginAndSigning.chooseSigningMethodTitle}</h2>
                <span className="login-method-step">{format(translated.loginAndSigning.step, 2)}</span>
            </div>
            <span className="login-method-description">
                {translated.loginAndSigning.chooseSigningMethodDescription}
            </span>
            <SelectPanel
                isSelected={signingMethodState === 'BANKID'}
                icon={IconTypes.BANK_ID_BRIKKE_36}
                disabled={mutateSigningMethod.isPending}
                onClick={() => {
                    setSigningMethodState('BANKID');
                    setNoSigningMethodChosen(false);
                }}
                label={translated.loginAndSigning.signingMethods.BANKID}
                trackId={settingsTrackIDs.signingMethodLink}
            />
            {pinSigningEnabled && (
                <SelectPanel
                    isSelected={signingMethodState === 'PIN'}
                    icon={IconTypes.PIN_KODE_36}
                    onClick={() => {
                        setSigningMethodState('PIN');
                        setNoSigningMethodChosen(false);
                    }}
                    label={translated.loginAndSigning.signingMethods.PIN}
                    trackId={settingsTrackIDs.signingMethodLink}
                />
            )}
            {biometricCapabilities && (
                <SelectPanel
                    isSelected={signingMethodState === 'BIOMETRIC'}
                    icon={IconTypes.BIOMETRI_36}
                    onClick={() => {
                        setSigningMethodState('BIOMETRIC');
                        setNoSigningMethodChosen(false);
                    }}
                    label={translated.loginAndSigning.signingMethods.BIOMETRIC}
                    disabled={loginMethod?.type !== 'BIOMETRIC'}
                    trackId={settingsTrackIDs.signingMethodLink}
                />
            )}
            {!biometricsEnabledApp && loginMethod?.type !== 'BIOMETRIC' && (
                <InfoMessage text={translated.loginAndSigning.infoMessageEnableBiometry} icon={IconTypes.INFO_36} />
            )}
            {showErrorMessage && (
                <ErrorMessage
                    text={translated.loginAndSigning.errorMessageBiometryDisabled}
                    icon={IconTypes.UTROPSTEGN_24}
                />
            )}
            {(mutateSigningMethod.isError || signingMethod?.error) && (
                <ErrorMessage
                    text={translated.loginAndSigning.errorMessageSigningMethod}
                    icon={IconTypes.UTROPSTEGN_24}
                />
            )}
            {noSigningMethodChosen && (
                <ErrorMessage
                    text={translated.loginAndSigning.errorMessageNoSigningMethodChosen}
                    icon={IconTypes.UTROPSTEGN_24}
                />
            )}
            <div className="signing-method-button-parent">
                <Button
                    buttonType={ButtonTypes.LARGE_NEGATIVE}
                    className="signing-method-button"
                    onClick={() => navigate(-1)}
                >
                    {translated.previous}
                </Button>
                <ButtonWithSpinner
                    showSpinner={mutateSigningMethod.isPending}
                    onClick={handleNavigateToNextStep}
                    className="signing-method-button"
                    buttonType="positive"
                >
                    {translated.loginAndSigning.saveAndComplete}
                </ButtonWithSpinner>
            </div>
            <LinkButton className="signing-method-button-back" onClick={resetNavigation}>
                {translated.cancel}
            </LinkButton>
        </>
    );
};

export default SelectSigningMethod;
